import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { useState } from 'react';
import { Control, FieldErrors, FieldValues } from 'react-hook-form';

import { TextFormField } from '~components/FormFields/TextFormField';
import { PseudoLink } from '~components/Helpers/PseudoLink';

interface VendorAccountDetailsProps<TFieldValues extends FieldValues = FieldValues> {
  control: Control<TFieldValues>;
  errors: FieldErrors<TFieldValues>;
  isEditing?: boolean;
}

export default function VendorAccountDetails<
  TFieldValues extends FieldValues = FieldValues,
>({ control, errors, isEditing = false }: VendorAccountDetailsProps<TFieldValues>) {
  const theme = useTheme();
  const [showVendorAccountDetails, setShowVendorAccountDetails] = useState(isEditing);

  return (
    <Box
      sx={{
        border: `solid 1px ${theme.brandV2.colors.treadGray7}`,
        borderRadius: '4px',
        p: 1,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography
          variant="body1"
          sx={{
            fontWeight: 600,
          }}
        >
          {t('user.form.vendor_account_details')}
        </Typography>
        <PseudoLink
          title={
            showVendorAccountDetails
              ? t('user.form.hide_details')
              : t('user.form.show_details')
          }
          action={() => setShowVendorAccountDetails(!showVendorAccountDetails)}
        />
      </Box>
      {showVendorAccountDetails && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Typography variant="body2">
            {t('user.form.vendor_account_details_hint')}
          </Typography>

          <TextFormField
            control={control}
            errors={errors}
            name="accountName"
            label={t('form_fields.account_name')}
            isRequired={isEditing}
          />

          <TextFormField
            control={control}
            errors={errors}
            name="accountExternalId"
            label={t('form_fields.external_id')}
            isRequired={false}
          />
        </Box>
      )}
    </Box>
  );
}
